import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import {
  Line, BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, AreaChart, Area, Cell, ReferenceLine,
} from "recharts";
import { toast } from "sonner";
import { Plus, Loader2, Target, Flame } from "lucide-react";

export default function ProgressPage() {
  const [weights, setWeights] = useState([]);
  const [calorieDays, setCalorieDays] = useState([]);
  const [adherence, setAdherence] = useState(null);
  const [newWeight, setNewWeight] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [w, f, a] = await Promise.all([
      api.get("/weight/history?days=30"),
      api.get("/food/history?days=14"),
      api.get("/workouts/adherence?weeks=8"),
    ]);
    setWeights(w.data.items);
    setCalorieDays(
      f.data.days.map((d) => ({
        ...d,
        label: new Date(d.date).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
      }))
    );
    setAdherence(a.data);
  };

  useEffect(() => {
    load();
  }, []);

  const logWeight = async (e) => {
    e.preventDefault();
    if (!newWeight) return;
    setSaving(true);
    try {
      await api.post("/weight/log", { weight_kg: Number(newWeight) });
      setNewWeight("");
      await load();
      toast.success("Weight logged");
    } catch {
      toast.error("Failed");
    } finally {
      setSaving(false);
    }
  };

  const weightChartData = weights.map((w) => ({
    label: new Date(w.logged_on).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
    weight: w.weight_kg,
  }));

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto fade-up" data-testid="progress-page">
      <div className="mb-10">
        <div className="label-eyebrow text-[#6B9BFF] mb-2">Progress</div>
        <h1 className="font-display text-4xl sm:text-5xl leading-none">Your trend</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="border border-white/10 bg-[#121212] p-6 lg:col-span-2" data-testid="weight-chart-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="label-eyebrow text-white/50">Weight (kg)</div>
              <div className="font-display text-2xl mt-1">Last 30 days</div>
            </div>
          </div>
          <div className="h-64">
            {weightChartData.length === 0 ? (
              <div className="h-full flex items-center justify-center text-white/40 text-sm">
                No weight entries yet. Log your first weight to see the chart.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={weightChartData}>
                  <defs>
                    <linearGradient id="wfill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#6B9BFF" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#6B9BFF" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="label" stroke="#71717A" fontSize={11} />
                  <YAxis stroke="#71717A" fontSize={11} domain={["dataMin - 2", "dataMax + 2"]} />
                  <Tooltip
                    contentStyle={{ background: "#121212", border: "1px solid rgba(255,255,255,0.1)", color: "#fff" }}
                  />
                  <Area type="monotone" dataKey="weight" stroke="#6B9BFF" strokeWidth={3} fill="url(#wfill)" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="border border-white/10 bg-[#121212] p-6" data-testid="weight-log-card">
          <div className="label-eyebrow text-white/50 mb-2">Log today's weight</div>
          <div className="font-display text-2xl mb-6">Stay accountable</div>
          <form onSubmit={logWeight} className="space-y-3">
            <div className="flex">
              <input
                type="number"
                step="0.1"
                required
                value={newWeight}
                onChange={(e) => setNewWeight(e.target.value)}
                placeholder="75.5"
                className="flex-1 bg-[#1A1A1A] border border-white/10 px-4 py-3 focus:outline-none focus:border-[#6B9BFF]"
                data-testid="weight-input"
              />
              <span className="bg-[#1A1A1A] border-y border-r border-white/10 px-4 py-3 text-white/50 text-sm flex items-center">
                kg
              </span>
            </div>
            <button
              disabled={saving}
              type="submit"
              className="w-full bg-[#6B9BFF] text-black font-black uppercase tracking-widest py-3 hover:bg-white transition-colors inline-flex items-center justify-center gap-2"
              data-testid="weight-submit-button"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Log weight
            </button>
          </form>
          {weights.length > 0 && (
            <div className="mt-6 pt-6 border-t border-white/5 text-sm">
              <div className="text-white/50">Latest</div>
              <div className="font-display text-3xl mt-1">
                {weights[weights.length - 1].weight_kg}
                <span className="text-lg text-white/40 ml-1">kg</span>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="border border-white/10 bg-[#121212] p-6" data-testid="calorie-chart-card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="label-eyebrow text-white/50">Calories</div>
            <div className="font-display text-2xl mt-1">Last 14 days</div>
          </div>
        </div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={calorieDays}>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="label" stroke="#71717A" fontSize={11} />
              <YAxis stroke="#71717A" fontSize={11} />
              <Tooltip
                contentStyle={{ background: "#121212", border: "1px solid rgba(255,255,255,0.1)", color: "#fff" }}
              />
              <Bar dataKey="calories" fill="#6B9BFF" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Adherence */}
      <div className="mt-6 border border-white/10 bg-[#121212] p-6" data-testid="adherence-card">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="label-eyebrow text-[#6B9BFF] mb-2 flex items-center gap-2">
              <Target className="w-3.5 h-3.5" /> Workout adherence
            </div>
            <div className="font-display text-2xl">Sessions vs plan · last 8 weeks</div>
            <p className="text-white/60 text-sm mt-2 max-w-md">
              Each bar is a week. Filled portion = completed sessions, faded = remaining vs your planned {adherence?.planned_per_week ?? "—"} days/week.
            </p>
          </div>
          {adherence && (
            <div className="flex gap-4">
              <div className="border border-white/10 bg-[#0a0a0a] px-4 py-3 text-center" data-testid="adherence-overall">
                <div className="label-eyebrow text-white/40 text-[10px]">Overall</div>
                <div className="font-display text-3xl text-[#6B9BFF]">{adherence.overall_pct}%</div>
              </div>
              <div className="border border-white/10 bg-[#0a0a0a] px-4 py-3 text-center" data-testid="adherence-streak">
                <div className="label-eyebrow text-white/40 text-[10px] flex items-center justify-center gap-1">
                  <Flame className="w-3 h-3" /> Streak
                </div>
                <div className="font-display text-3xl text-[#FF3B30]">{adherence.current_streak_weeks}<span className="text-base text-white/40 ml-1">wk</span></div>
              </div>
            </div>
          )}
        </div>

        {!adherence || adherence.weeks.length === 0 ? (
          <div className="h-48 flex items-center justify-center text-white/40 text-sm">
            Complete your first workout session to start tracking adherence.
          </div>
        ) : (
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={adherence.weeks.map((w) => ({
                  label: new Date(w.week_start).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
                  completed: w.completed,
                  remaining: Math.max(0, w.planned - w.completed),
                  planned: w.planned,
                  pct: w.adherence_pct,
                  hit: w.completed >= w.planned,
                }))}
              >
                <CartesianGrid stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="label" stroke="#71717A" fontSize={11} />
                <YAxis stroke="#71717A" fontSize={11} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ background: "#121212", border: "1px solid rgba(255,255,255,0.1)", color: "#fff", fontSize: 12 }}
                  formatter={(val, name, item) => {
                    if (name === "completed") return [`${val} / ${item.payload.planned} sessions`, "Completed"];
                    if (name === "remaining") return [`${val}`, "Remaining"];
                    return [val, name];
                  }}
                  labelFormatter={(l) => `Week of ${l}`}
                />
                <ReferenceLine
                  y={adherence.planned_per_week}
                  stroke="#FFB020"
                  strokeDasharray="3 3"
                  label={{ value: "Plan", position: "right", fill: "#FFB020", fontSize: 10 }}
                />
                <Bar dataKey="completed" stackId="a">
                  {adherence.weeks.map((w) => (
                    <Cell key={w.week_start || w.week} fill={w.completed >= w.planned ? "#6B9BFF" : "#6B9BFF99"} />
                  ))}
                </Bar>
                <Bar dataKey="remaining" stackId="a" fill="rgba(255,255,255,0.06)" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}
